class CollectionTitleSearch extends HTMLElement {
  constructor() {
    super();

    this.handleInput = this.handleInput.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleGridUpdated = this.handleGridUpdated.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.input = this.querySelector('[data-title-search-input]');
    this.submitButton = this.querySelector('[data-title-search-submit]');
    this.clearButton = this.querySelector('[data-title-search-clear]');
    this.status = this.querySelector('[data-title-search-status]');
    if (!this.input || !this.submitButton || !this.clearButton || !this.status) return;

    this.initialized = true;
    this.state = CollectionTitleSearch.getState(this.dataset.sectionId);
    this.state.instances.add(this);
    this.debouncedSearch = this.debounce(() => this.search(false), 350);

    this.input.value = this.state.activeTerm;
    this.clearButton.hidden = this.state.activeTerm === '';
    this.status.textContent = this.state.statusText;

    this.input.addEventListener('input', this.handleInput);
    this.input.addEventListener('keydown', this.handleKeydown);
    this.submitButton.addEventListener('click', this.handleSubmit);
    this.clearButton.addEventListener('click', this.handleClear);
    document.addEventListener('collection:grid:updated', this.handleGridUpdated);
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.state.instances.delete(this);
    this.input.removeEventListener('input', this.handleInput);
    this.input.removeEventListener('keydown', this.handleKeydown);
    this.submitButton.removeEventListener('click', this.handleSubmit);
    this.clearButton.removeEventListener('click', this.handleClear);
    document.removeEventListener('collection:grid:updated', this.handleGridUpdated);
  }

  static getState(sectionId) {
    if (!this.states.has(sectionId)) {
      this.states.set(sectionId, {
        activeTerm: '',
        cacheKey: null,
        cachePromise: null,
        cachedCards: null,
        generation: 0,
        handlingGridUpdate: false,
        instances: new Set(),
        originalGridMarkup: null,
        statusText: '',
      });
    }

    return this.states.get(sectionId);
  }

  get gridContainer() {
    return document.getElementById('ProductGridContainer');
  }

  debounce(callback, wait) {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  }

  normalize(value) {
    return value.normalize('NFKC').toLocaleLowerCase('ja-JP').trim();
  }

  handleInput(event) {
    event.stopPropagation();
    this.state.activeTerm = this.input.value;
    this.syncInstances();

    if (this.normalize(this.state.activeTerm) === '') {
      this.clearSearch();
      return;
    }

    this.debouncedSearch();
  }

  handleKeydown(event) {
    event.stopPropagation();
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.search(true);
  }

  handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    this.search(true);
  }

  handleClear(event) {
    event.preventDefault();
    event.stopPropagation();
    this.clearSearch();
    this.input.focus();
  }

  handleGridUpdated() {
    if (this.state.handlingGridUpdate) return;

    this.state.handlingGridUpdate = true;
    queueMicrotask(() => {
      this.state.handlingGridUpdate = false;
    });

    this.state.generation += 1;
    this.state.originalGridMarkup = null;
    this.state.cacheKey = null;
    this.state.cachedCards = null;
    this.state.cachePromise = null;

    if (this.normalize(this.state.activeTerm) !== '') {
      this.search(false);
    }
  }

  syncInstances() {
    this.state.instances.forEach((instance) => {
      if (instance.input.value !== this.state.activeTerm) {
        instance.input.value = this.state.activeTerm;
      }
      instance.clearButton.hidden = this.normalize(this.state.activeTerm) === '';
    });
  }

  setStatus(text) {
    this.state.statusText = text;
    this.state.instances.forEach((instance) => {
      instance.status.textContent = text;
    });
  }

  setBusy(isBusy) {
    this.state.instances.forEach((instance) => {
      instance.toggleAttribute('aria-busy', isBusy);
    });
  }

  clearSearch() {
    this.state.generation += 1;
    this.state.activeTerm = '';
    this.syncInstances();
    this.setStatus('');
    this.setBusy(false);

    if (this.state.originalGridMarkup === null || !this.gridContainer) return;

    this.gridContainer.innerHTML = this.state.originalGridMarkup;
    this.state.originalGridMarkup = null;
    this.initializeGridFeatures();
  }

  async search(closeMobileDrawer) {
    const term = this.normalize(this.state.activeTerm);
    if (term === '') {
      this.clearSearch();
      return;
    }

    const container = this.gridContainer;
    if (!container) return;

    if (this.state.originalGridMarkup === null) {
      this.state.originalGridMarkup = container.innerHTML;
    }

    const generation = ++this.state.generation;
    this.syncInstances();
    this.setStatus('コレクション内の商品名を検索しています…');
    this.setBusy(true);

    try {
      const cards = await this.loadAllCards();
      if (generation !== this.state.generation) return;

      const matches = cards.filter((card) => this.normalize(card.title).includes(term));
      this.renderMatches(matches);
      this.setStatus(`${matches.length}件の商品が見つかりました`);
      if (closeMobileDrawer) this.closeMobileDrawer();
    } catch (error) {
      if (generation !== this.state.generation) return;

      this.restoreOriginalGrid();
      this.setStatus('商品名検索を完了できませんでした。時間をおいてもう一度お試しください。');
      console.error('Collection title search failed:', error);
    } finally {
      if (generation === this.state.generation) this.setBusy(false);
    }
  }

  async loadAllCards() {
    const key = this.getCacheKey();
    if (this.state.cacheKey === key && this.state.cachedCards) return this.state.cachedCards;
    if (this.state.cacheKey === key && this.state.cachePromise) return this.state.cachePromise;

    this.state.cacheKey = key;
    const container = this.gridContainer;
    const pagination = container.querySelector('[data-pagination-enhanced]');
    const totalPages = Number(pagination?.dataset.totalPages || 1);
    const currentPage = Number(pagination?.dataset.currentPage || 1);
    const cards = this.readCards(container);
    const pages = [];

    for (let page = 1; page <= totalPages; page += 1) {
      if (page !== currentPage) pages.push(page);
    }

    const promise = this.fetchPages(pages, 3)
      .then((pageCards) => {
        const cardsByPage = new Map([[currentPage, cards]]);
        pages.forEach((page, index) => cardsByPage.set(page, pageCards[index]));

        const orderedCards = [];
        for (let page = 1; page <= totalPages; page += 1) {
          orderedCards.push(...(cardsByPage.get(page) || []));
        }

        return this.deduplicateCards(orderedCards);
      })
      .then((allCards) => {
        if (this.state.cacheKey === key) this.state.cachedCards = allCards;
        return allCards;
      })
      .finally(() => {
        if (this.state.cacheKey === key) this.state.cachePromise = null;
      });

    this.state.cachePromise = promise;
    return promise;
  }

  async fetchPages(pages, concurrency) {
    const results = new Array(pages.length);
    let nextIndex = 0;

    const worker = async () => {
      while (nextIndex < pages.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await this.fetchPage(pages[index]);
      }
    };

    const workerCount = Math.min(concurrency, pages.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return results;
  }

  async fetchPage(page) {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('phcursor');
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', this.dataset.sectionId);

    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`Unable to load collection page ${page}: ${response.status}`);

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const grid = parsed.getElementById('product-grid');
    return grid ? this.readCards(grid) : [];
  }

  readCards(root) {
    return Array.from(root.querySelectorAll('li.collection-product-card')).map((card) => ({
      handle: card.dataset.productHandle || '',
      title: card.dataset.productTitle || card.querySelector('.card__title')?.textContent.trim() || '',
      html: card.outerHTML,
    }));
  }

  deduplicateCards(cards) {
    const seen = new Set();
    return cards.filter((card) => {
      const key = card.handle || card.html;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  getCacheKey() {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('page');
    url.searchParams.delete('phcursor');
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return `${this.dataset.sectionId}:${url.pathname}?${url.searchParams.toString()}`;
  }

  renderMatches(cards) {
    this.restoreOriginalGrid();
    const container = this.gridContainer;
    const grid = container?.querySelector('#product-grid');
    if (!grid) return;

    const fragment = document.createDocumentFragment();
    if (cards.length === 0) {
      const emptyItem = document.createElement('li');
      emptyItem.className = 'collection-title-search__empty';
      emptyItem.textContent = '該当する商品はありません。別の商品名で検索してください。';
      fragment.appendChild(emptyItem);
    } else {
      cards.forEach((card) => {
        const template = document.createElement('template');
        template.innerHTML = card.html.trim();
        const item = template.content.firstElementChild;
        if (item) fragment.appendChild(item);
      });
    }

    grid.replaceChildren(fragment);
    container
      .querySelectorAll(
        '[data-pagination-enhanced], .pagination-wrapper, .button-wrapper, .infinite-scroll__data, .infinite-scroll__loading'
      )
      .forEach((element) => element.classList.add('js-hidden'));
    container.querySelector('.collection')?.classList.remove('loading');
    this.initializeCardFeatures();
  }

  restoreOriginalGrid() {
    if (this.state.originalGridMarkup !== null && this.gridContainer) {
      this.gridContainer.innerHTML = this.state.originalGridMarkup;
    }
  }

  initializeCardFeatures() {
    if (typeof window.colorSwatches === 'function') window.colorSwatches();
  }

  initializeGridFeatures() {
    this.initializeCardFeatures();
    if (typeof window.loadMore === 'function') window.loadMore();
  }

  closeMobileDrawer() {
    if (!this.classList.contains('collection-title-search--mobile')) return;

    const drawerDetails = this.closest('menu-drawer')?.querySelector(':scope > details');
    if (drawerDetails?.open) drawerDetails.querySelector(':scope > summary')?.click();
  }
}

CollectionTitleSearch.states = new Map();

if (!customElements.get('collection-title-search')) {
  customElements.define('collection-title-search', CollectionTitleSearch);
}
